/**
 * 统计模块
 * @description 包含 灵犀、Raptor 异常上报、Raptor metric 上报
 */
// tslint:disable:trailing-comma align

import { getProp } from '@mx/util/get-prop';
import neoMetric from './neo-metric';
import { LXAnalyticsChannel } from './channel/lxanalytics-channel';
import { RaptorChannel } from './channel/raptor-channel';
import {
  BridgeBids,
  ParamsTunnelBids,
  PrefetchDataTunnelBids,
  NeoBids,
  DebugBids,
} from './lx-constants';
import { env } from '../context/env';
import neoParams from '../context/params';
import {
  StatChannel,
  StatReportType,
  StatErrorLevel,
  StatErrorCategory,
  LXStatSendParams,
  RaptorSendParams,
  MetricSendParams,
  BridgeSendType,
} from './constants';
import { setSessionStorage, hasSessionStorage } from '../util/storage';
import { NeoError } from '../util/neo-error';
import { debug, DebugLevel } from '../util/debug';
import { NeoNamespace } from '../common-enum';

interface NeoStatConfig {
  // 统计基础参数
  statBasicParams: any;
  // 获取统计基础参数
  mapStatBasicParams?: (params: any) => any;
}

export interface ReportChannelParams {
  lxParams?: LXStatSendParams;
  raptorParams?: RaptorSendParams;
  metricParams?: MetricSendParams;
}

interface LXParams {
  bid?: string;
  lab?: any;
  opts?: any;
}

// 公共基础参数
interface BasicParams {
  /* eslint-disable */
  project: string;
  app_name: string;
  app_version: string;
  nb_version: string;
  nb_platform: string;
  network_env: string;
  is_offline: number;
  nb_container: string;
  current_scheme: string;
  current_url: string;
  last_resumed_page: string;
  is_debug: number;
  /* eslint-enable */
}

export class NeoStat {
  private lxStat!: LXAnalyticsChannel;

  private raptorStat!: RaptorChannel;

  private mapStatBasicParams?: (params: any) => any;

  // 全量基础数据
  private basicParams: BasicParams = {} as BasicParams;

  // 灵犀上报通用数据
  private lxLabParams: any = {};

  // Raptor 上报通用数据
  private raptorParams!: any;

  // 渲染检查上报数据
  private renderCheckParams = {} as any;

  constructor() {
    this.lxStat = new LXAnalyticsChannel();
    this.raptorStat = new RaptorChannel();
  }

  init(config: NeoStatConfig): void {
    debug(NeoNamespace.Stat, DebugLevel.Info, 'stat/index 模块初始化');

    const envProps = env.getEnv();

    this.mapStatBasicParams = config.mapStatBasicParams;

    // 设置配置参数
    this.basicParams = {
      nb_container: 'hybrid',
      app_name: envProps.appName,
      network_env: envProps.network,
      is_offline: envProps.isOffline ? 1 : 0,
      current_scheme: window.location.href,
      current_url: window.location.origin + window.location.pathname,
      ...config.statBasicParams,
    };

    this.lxLabParams = { ...this.basicParams };
    this.raptorParams = { ...this.basicParams };
  }

  /**
   * 映射初始化参数至统计基础参数
   */
  mapBasicParams(): void {
    const params = neoParams.getParams();
    const envProps = env.getEnv();
    const customBasicParams = this.mapStatBasicParams
      ? this.mapStatBasicParams.call(null, params)
      : {};

    this.basicParams = {
      ...this.basicParams,
      app_version: params.app_version || '',
      nb_platform: params.device_platform || '',
      nb_version: params.app_pay_sdk_version || '',
      is_debug: params.is_debug || 0,
      last_resumed_page: customBasicParams.last_resumed_page || '',
    };

    const statParams = {
      web_container: envProps.container,
      ...this.basicParams,
      ...customBasicParams,
      // TODO Neo JS 版本号
      neo_js_version: '0.1.0',
      // 设置环境参数
      unique_id: envProps.neoUniqueId,
      reload_type: +envProps.isPageReload,
    };
    // 设置灵犀上报字段
    this.lxLabParams = {
      device_rooted: params.device_rooted,
      priority: 'normal',
      technology_log_version: params.app_pay_sdk_version || '',
      ...statParams,
    };
    // 设置 Raptor 异常上报字段
    this.raptorParams = {
      ...statParams,
    };
    // 设置自定义指标上报字段(纬度)
    neoMetric.setTags({
      ...this.basicParams,
    });
    neoMetric.setStatTimeAndReport(params.stat_time);
    // 发送所有延迟上报埋点
    neoMetric.sendDelayReportList();
  }

  /**
   * 数据上报
   * @param channel 上报渠道
   * @param data 上报数据
   * @param channelParam 额外渠道信息
   */
  report(
    channel: StatChannel,
    labOrTags: any = {},
    channelParam: ReportChannelParams = {} as ReportChannelParams
  ): void {
    if (!this.lxStat || !this.raptorStat) {
      throw new NeoError(
        NeoNamespace.Stat,
        '统计模块尚未初始化，确认 stat.init() 被调用'
      );
    }

    const {
      cid = '',
      bid = '',
      enviroment = '',
      options = {},
      type = '',
    } = channelParam.lxParams || {};

    // 灵犀上报时，所有数据字段统一放入 valLab.custom 中
    // https://ocean.sankuai.com/config/#/iframe/lx
    let lab = {};
    if (type === StatReportType.PV) {
      lab = {
        custom: {
          ...labOrTags,
          ...this.lxLabParams,
        },
      };
    } else {
      lab = {
        ...labOrTags,
        ...this.lxLabParams,
      };
    }

    const sendLX = () =>
      this.lxStat.send({
        cid,
        bid,
        type,
        lab,
        options: {
          cid,
          ...options,
        },
        enviroment,
      } as LXStatSendParams);
    const sendRaptor = () =>
      this.raptorStat.send({
        ...channelParam.raptorParams,
        tags: {
          ...this.raptorParams,
          ...labOrTags,
          ...channelParam.raptorParams?.tags,
        },
      } as RaptorSendParams);
    const sendMetric = () => {
      const {
        key = '',
        value = 1,
        tags = {},
        isNative = false,
      } = channelParam.metricParams || {};
      neoMetric.report({
        key,
        value: Number.isNaN(value) ? 1 : value,
        tags: {
          ...labOrTags,
          ...tags,
        },
        isNative,
      });
    };

    switch (channel) {
      case StatChannel.LXAnalyticsAndMetric:
        sendLX();
        sendMetric();
        break;
      case StatChannel.LXAnalytics:
        sendLX();
        break;
      case StatChannel.Raptor:
        sendRaptor();
        break;
      case StatChannel.Metric:
        sendMetric();
        break;
      default:
        debug(
          NeoNamespace.StatLXAnalyticsChannel,
          DebugLevel.Error,
          'stat.report: 上报渠道未指定'
        );
        break;
    }
  }

  // report 是否可以覆盖 sendToLX 逻辑，两者并存的意义，once 是否需要在 report 中实现
  /**
   * 上报到灵犀
   * @param info 数据
   * @param bid bid
   * @param once 点位只被上报一次
   */
  sendToLX(
    bid: string,
    info: any = {},
    type: StatReportType = StatReportType.SC,
    once = false
  ) {
    debug(NeoNamespace.Stat, DebugLevel.Info, `sendToLX =  bid: ${bid}`, info);
    if (once) {
      if (hasSessionStorage(bid)) {
        return;
      }
      setSessionStorage(bid);
    }
    this.report(
      StatChannel.LXAnalytics,
      {
        ...info,
      },
      {
        lxParams: {
          cid: 'c_pay_neo_js_sdk',
          bid,
          type,
        },
      }
    );
  }

  /**
   * 上报到 Raptor平台
   * @param error 错误对象或者错误信息
   * @param tags 额外数据字段
   * @param level 等级
   * @param category 分类
   */
  sendToRaptor(
    error: any,
    tags: any,
    level: StatErrorLevel = StatErrorLevel.Info,
    category: StatErrorCategory = StatErrorCategory.JSError
  ) {
    this.report(
      StatChannel.Raptor,
      {},
      {
        raptorParams: {
          error,
          level,
          category,
          tags,
        },
      }
    );
  }

  /**
   * 上报自定义指标
   * @param key 纬度
   * @param value 数值
   * @param tags 其他的额度
   * @param isNative 是否上报到 native 通道
   */
  sendMetric(key: string, value: number = 1, tags: any = {}, isNative = false) {
    this.report(
      StatChannel.Metric,
      {},
      {
        metricParams: {
          key,
          value,
          tags,
          isNative,
        },
      }
    );
  }

  /**
   * 调用桥相关埋点
   */
  sendBridgeInfo(sendType: BridgeSendType, data: LXParams = {} as any) {
    if (!sendType) {
      return;
    }
    try {
      let bid = '';
      let reportKey = '';

      switch (sendType) {
        case BridgeSendType.start:
          bid = BridgeBids.BridgeStart;
          reportKey = 'neo_call_bridge_start';
          break;
        case BridgeSendType.success:
          bid = BridgeBids.BridgeSuccess;
          reportKey = 'neo_call_bridge_success';
          break;
        case BridgeSendType.active:
          bid = BridgeBids.BridgeExecuteSuccess;
          reportKey = 'neo_call_bridge_active';
          break;
        case BridgeSendType.inactive:
          bid = BridgeBids.BridgeExecuteFail;
          reportKey = 'neo_call_bridge_inactive';
          break;
        default:
          break;
      }

      if (bid) {
        this.sendToLX(bid, {
          ...data.lab,
        });
      }

      if (reportKey) {
        this.sendMetric(reportKey, getProp(data, ['lab', 'bridge_time'], 1), {
          bridge_name: getProp(data, ['lab', 'bridge_name']),
        });
      }

      // 20200413新增BridgeErrorCode的jsError监控埋点
      if (sendType === BridgeSendType.inactive) {
        this.sendToRaptor(
          'BridgeErrorCode',
          {
            ...data.lab,
          },
          StatErrorLevel.Error,
          StatErrorCategory.JSError
        );
      }
    } catch (e) {
      debug(NeoNamespace.Stat, DebugLevel.Error, e);
    }
  }
}

const stat = new NeoStat();

export {
  stat,
  StatChannel,
  StatReportType,
  StatErrorLevel,
  StatErrorCategory,
  BridgeSendType,
  ParamsTunnelBids,
  PrefetchDataTunnelBids,
  NeoBids,
  DebugBids,
};
export default stat;
